import { NOYAU_CODES, type NoyauCode, type ResumeTraduction } from '@rosetta/shared';

/**
 * One card of the history (#11): the translations of one document (one text, translated towards
 * other noyaux later) are one card. It is a `ResumeTraduction`, so the search, the filter and the
 * cards of #4, #5 and #7 work on it as they are; what it adds says which translations it stands
 * for.
 */
export interface CarteHistorique extends ResumeTraduction {
  /** Every translation of the document, the most recent first (the card's `id` is one of them). */
  lancements: readonly string[];
  /**
   * While noyaux are being added to a text already translated (H9): the noyaux of the running
   * translation that are not translated yet. Their pill shows "en cours".
   */
  noyauxAjoutes?: readonly NoyauCode[];
  /**
   * For each added noyau (H9, #36), the running translation that works on it: the pill of that
   * noyau is a link to its waiting screen.
   */
  lancementsAjoutes?: Readonly<Partial<Record<NoyauCode, string>>>;
  /**
   * For each noyau already translated (#36), the most recent translation of the document that
   * carries it (finished or partial): the pill of that noyau is a link to its result, with that
   * noyau selected.
   */
  lancementsTraduits?: Readonly<Partial<Record<NoyauCode, string>>>;
  /**
   * While noyaux are being added (H9): the finished translation whose result the title opens; the
   * card's `id` is then the running one, the one "interrompre" stops.
   */
  idResultat?: string;
}

const enOrdre = (noyaux: Iterable<NoyauCode>): NoyauCode[] => {
  const ensemble = new Set(noyaux);
  return NOYAU_CODES.filter((noyau) => ensemble.has(noyau));
};

const plusRecentDAbord = (a: ResumeTraduction, b: ResumeTraduction): number =>
  b.creeLe.localeCompare(a.creeLe) || Number(b.id) - Number(a.id) || b.id.localeCompare(a.id);

/**
 * Groups the translations of the history by document (#11, decision of 2026-10-08: by document
 * only, never by identical text), the most recent card first. A card takes the date of the most
 * recent translation of its document, and:
 *  - without a finished translation, it is the card of its most recent running translation (H6),
 *    or of its most recent translation (H5, H7) as before #11;
 *  - with one, it is the card of the text translated (H8, H10): the noyaux of its finished
 *    translations, "autre noyau", and the result of the most recent of them;
 *  - with one and a translation running, it is the card of an add (H9): the noyaux already
 *    translated, the added ones "en cours", "interrompre" for the running translation only, and
 *    the title towards the result of what is already translated.
 * A translation that failed or was interrupted after a finished one leaves the card as it was:
 * the choice of the noyaux says what became of each noyau.
 */
export function regrouperParDocument(traductions: readonly ResumeTraduction[]): CarteHistorique[] {
  const parDocument = new Map<string, ResumeTraduction[]>();
  for (const traduction of traductions) {
    const cle = traduction.documentId ?? `traduction:${traduction.id}`;
    parDocument.set(cle, [...(parDocument.get(cle) ?? []), traduction]);
  }

  const cartes: CarteHistorique[] = [];
  for (const groupe of parDocument.values()) {
    const recents = [...groupe].sort(plusRecentDAbord);
    const [dernier] = recents;
    if (dernier === undefined) continue;
    const lancements = recents.map((traduction) => traduction.id);
    const finies = recents.filter((traduction) => traduction.statut === 'terminee');
    const enCours = recents.filter((traduction) => traduction.statut === 'en_cours');
    const [derniereFinie] = finies;
    const [derniereEnCours] = enCours;
    // The text, its title and its date: those of the most recent translation of the document.
    const lancementsTraduits: Partial<Record<NoyauCode, string>> = {};
    for (const traduction of recents) {
      if (traduction.statut !== 'terminee' && traduction.statut !== 'partielle') continue;
      for (const noyau of traduction.noyaux) lancementsTraduits[noyau] ??= traduction.id;
    }
    const commun = { creeLe: dernier.creeLe, lancements, lancementsTraduits };

    if (derniereFinie === undefined) {
      cartes.push({ ...(derniereEnCours ?? dernier), ...commun });
      continue;
    }

    const traduits = enOrdre(finies.flatMap((traduction) => traduction.noyaux));
    if (derniereEnCours === undefined) {
      cartes.push({ ...derniereFinie, ...commun, noyaux: traduits });
      continue;
    }

    const ajoutes = enOrdre(enCours.flatMap((traduction) => traduction.noyaux)).filter(
      (noyau) => !traduits.includes(noyau),
    );
    // The most recent running translation of a noyau is the one its pill leads to (#36).
    const lancementsAjoutes: Partial<Record<NoyauCode, string>> = {};
    for (const traduction of enCours) {
      for (const noyau of ajoutes) {
        if (traduction.noyaux.includes(noyau)) lancementsAjoutes[noyau] ??= traduction.id;
      }
    }
    cartes.push({
      ...derniereEnCours,
      ...commun,
      noyaux: enOrdre([...traduits, ...ajoutes]),
      noyauxAjoutes: ajoutes,
      lancementsAjoutes,
      idResultat: derniereFinie.id,
    });
  }
  return cartes.sort(plusRecentDAbord);
}
